/**
 * Point Load Test — client-side live preview of the computed columns.
 *
 * SYNC-POINTER: mirrors backend/src/calculation/pointLoadTestCalculations.ts.
 * Keep both in step. The backend recompute on save is authoritative; this is
 * purely so the grid shows results as the technician types.
 */
import type { PltComputedFields, PltInputFields } from '../../../types/pointLoadTest';

const PSI_TO_KPA = 6.89476;
export const PLT_LOAD_AREA_DEFAULT_M2 = 0.0021;
export const PLT_K_FACTOR_DEFAULT = 23;
const DE_REF_MM = 50;
const F_EXPONENT = 0.45;

export const PLT_VALIDITY_CODE_MAP: Record<string, string> = {
  'Valid - Unknown': 'V -',
  'Valid - Intact Rock': 'V R',
  'Valid - Rock Fabric': 'V F',
  'Valid - Microdefect': 'V M',
  'Invalid - Chipping': 'I C',
  'Invalid - Punching': 'I P',
};

function num(v: number | null | undefined): number | null {
  return v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v);
}

export function buildTypeDirectionCode(type: string | null, dir: string | null): string | null {
  const t = (type ?? '').trim();
  const d = (dir ?? '').trim();
  if (!t && !d) return null;
  return `${t} ${d}`.trim();
}

export function buildValidityCode(validity: string | null): string | null {
  const v = (validity ?? '').trim();
  if (!v) return null;
  return PLT_VALIDITY_CODE_MAP[v] ?? '';
}

export function isValidValidity(validity: string | null | undefined): boolean {
  return !!validity && validity.startsWith('Valid');
}

/** De² (mm²) — type- and D'-dependent branch (template T-column IF). */
export function computeDe2(
  testType: string | null,
  widthMm: number | null,
  platenDistanceMm: number | null,
  platenDistanceFailureMm: number | null,
): number | null {
  const isDiametral = (testType ?? '').toUpperCase() === 'D';
  const W = num(widthMm);
  const D = num(platenDistanceMm);
  const Dp = num(platenDistanceFailureMm);
  if (Dp === null) {
    if (isDiametral) return D === null ? null : D * D;
    return D === null || W === null ? null : (4 * D * W) / Math.PI;
  }
  if (isDiametral) return D === null ? null : D * Dp;
  return W === null ? null : (4 * Dp * W) / Math.PI;
}

/** Full preview of all computed columns for one row. */
export function previewPointLoad(input: PltInputFields): PltComputedFields {
  const psi = num(input.gaugePsi);
  const gaugeKpa = psi === null ? null : psi * PSI_TO_KPA;
  const area = num(input.loadAreaM2) ?? PLT_LOAD_AREA_DEFAULT_M2;
  const failureLoadKn = gaugeKpa === null ? null : gaugeKpa * area;
  const de2Mm2 = computeDe2(input.testType, input.widthMm, input.platenDistanceMm, input.platenDistanceFailureMm);
  const deMm = de2Mm2 === null || de2Mm2 < 0 ? null : Math.sqrt(de2Mm2);
  const isMpa = failureLoadKn === null || de2Mm2 === null || de2Mm2 <= 0 ? null : (failureLoadKn * 1000) / de2Mm2;
  const sizeCorrectionF = deMm === null || deMm <= 0 ? null : (deMm / DE_REF_MM) ** F_EXPONENT;
  const is50Mpa = isMpa === null || sizeCorrectionF === null ? null : isMpa * sizeCorrectionF;
  const k = num(input.kFactor) ?? PLT_K_FACTOR_DEFAULT;
  const ucsMpa = is50Mpa === null ? null : is50Mpa * k;
  return {
    testTypeDirectionCode: buildTypeDirectionCode(input.testType, input.loadDirection),
    validityCode: buildValidityCode(input.validity),
    gaugeKpa,
    failureLoadKn,
    de2Mm2,
    deMm,
    isMpa,
    sizeCorrectionF,
    is50Mpa,
    ucsMpa,
  };
}
